import {Component, Input, OnInit} from '@angular/core';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {GatewayService} from '@services/administration/gateway/gateway.service';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {
  GatewayCommunicationRequest
} from '@models/adminstration/gateway/gateway-communication/gateway-communication-request';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {GATEWAY_TYPE} from '@constants/gateway/gateway-type';
import {
  GatewayCommunicationRequestNames,
  GatewayCommunicationRequestTypes
} from '@constants/administration/gateway/gateway-communication.enums';

@Component({
    selector: 'scitis-gateway-communication-modal',
    templateUrl: './gateway-communication-modal.component.html',
    styleUrls: ['./gateway-communication-modal.component.scss'],
    standalone: false
})
export class GatewayCommunicationModalComponent implements OnInit {
  @Input()
  public gateway: Gateway = new Gateway();
  public gatewayCommunicationForm: UntypedFormGroup = new UntypedFormGroup({});
  public requestSent: boolean = false;
  public requestNames: string[] = Object.values(GatewayCommunicationRequestNames);
  public requestTypes: string[] = Object.values(GatewayCommunicationRequestTypes);
  private positiveIntegersOrNullRegexPattern = new RegExp(/(^$)|(^[+]?\d+$)|(null)/);
  private requestDetails: GatewayCommunicationRequest = new GatewayCommunicationRequest();

  private COMPONENT_NAME: string = 'GatewayCommunicationModalComponent';
  private selectedRequestType: string = GatewayCommunicationRequestTypes.CONFIGURATION;

  constructor(private activeModal: NgbActiveModal,
              private gatewayService: GatewayService,
              private translate: TranslateService,
              private loadingIndicator: LoadingIndicatorService,
              private notification: NotificationService) {
  }

  public get isCommandRequest(): boolean {
    return this.selectedRequestType === GatewayCommunicationRequestTypes.COMMAND;
  }

  get f() {
    return this.gatewayCommunicationForm.controls;
  }

  ngOnInit(): void {
    this.generateGatewayCommunicationForm();
  }

  onClose() {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel(_$event: boolean) {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onGatewayCommunication($event: boolean) {
    if (!$event) {
      return;
    }
    const gatewayType = this.gateway.cloudplugType;
    if (gatewayType != GATEWAY_TYPE.ARGOS_GATEWAY && gatewayType != GATEWAY_TYPE.GENERIC) {
      const message = this.translate.instant('Gateway send command/configuration functionality not supported for %type gateway type').replace('%type', gatewayType.toBold());
      this.notification.warning(message, this.COMPONENT_NAME);
      return;
    }

    this.requestSent = true;
    this.loadingIndicator.show();
    this.getRequestValues();
    this.gatewayService.gatewayCommunication(this.gateway.id, this.requestDetails).subscribe((_success) => {

      this.notification.success(this.translate.instant('Gateway send command/configuration requested successfully'), this.COMPONENT_NAME, false);
      this.requestSent = false;
      this.loadingIndicator.hide();
      this.activeModal.close(this.requestDetails);
    }, (failure) => {
      const title = this.translate.instant('Error sending command/configuration');
      const message = this.parseErrorAndGetMessage(failure);

      this.notification.error(message, failure, this.COMPONENT_NAME, true, title);
      this.requestSent = false;
      this.loadingIndicator.hide();
    });


  }

  public isServicePerformedChanged() {
    this.f.servicePerformed.setValue(!this.f.servicePerformed.value);
  }

  public onRequestNameChange($event: string) {
    this.f.requestName.setValue($event);
  }

  public onRequestTypeChange($event: string) {
    this.selectedRequestType = $event;
    this.f.requestType.setValue($event);

  }

  private parseErrorAndGetMessage(failure): string {
    if (!failure || !failure.error) {
      return '';
    }
    let error = failure.error;
    const index = error.indexOf('{');
    if (index >= 0) {
      error = error.substring(index);
      const errorObject = JSON.parse(error);
      if (!errorObject) {
        return '';
      }
      return `${errorObject.status} ${errorObject.message}`;
    }
    return error;


  }

  private generateGatewayCommunicationForm() {
    this.gatewayCommunicationForm = new UntypedFormGroup({
      requestName: new UntypedFormControl(this.requestDetails.requestName, [Validators.required]),
      requestType: new UntypedFormControl(this.selectedRequestType, [Validators.required]),
      offsetHours: new UntypedFormControl(this.requestDetails.offsetHours, [Validators.pattern(this.positiveIntegersOrNullRegexPattern), Validators.min(0)]),
      serviceInterval: new UntypedFormControl(this.requestDetails.serviceInterval, [Validators.pattern(this.positiveIntegersOrNullRegexPattern), Validators.min(0)]),
      servicePerformed: new UntypedFormControl(this.requestDetails.servicePerformed)
    });
  }

  private getRequestValues() {
    this.requestDetails.configurationRequest = this.f.requestType.value === GatewayCommunicationRequestTypes.CONFIGURATION;
    this.requestDetails.requestName = this.f.requestName.value;
    this.requestDetails.servicePerformed = this.f.servicePerformed.value;
    this.requestDetails.offsetHours = this.f.offsetHours.value;
    this.requestDetails.serviceInterval = this.f.serviceInterval.value;
    this.requestDetails.timestamp = Math.floor(new Date().getTime() / 1000);
  }
}
